import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { getVehicleById, updateVehicle } from '../services/vehicleService';
import { useAuth } from '../context/AuthContext';

// Form Bileşenleri
import VehicleBasicInfoForm from '../components/forms/VehicleBasicInfoForm';
import VehicleTechnicalForm from '../components/forms/VehicleTechnicalForm';
import VehicleDetailedTechnicalForm from '../components/forms/VehicleDetailedTechnicalForm';
import VehicleBodyConditionForm from '../components/forms/VehicleBodyConditionForm';
import VehiclePhotoUpload from '../components/forms/VehiclePhotoUpload';
import VehicleAdditionalForm from '../components/forms/VehicleAdditionalForm';

const EditVehiclePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    // Using currentUser for authorization checks later in the component
    const { currentUser } = useAuth();

    // API'den alınacak araç verisi için başlangıç durumu
    const initialVehicleState = {
        title: '',
        brand: '',
        model: '',
        year: '',
        price: '',
        mileage: '',
        fuelType: 'Benzin',
        transmission: 'Manuel',
        color: '',
        description: '',
        features: [],
        location: {
            city: '',
            district: ''
        },
        imageUrl: null,
        images: [],

        // Yeni eklenecek alanlar
        generalFeatures: {
            acceleration: '',
            enginePower: '',
            maxSpeed: '',
        },
        engineDetails: {
            power: '',
            volume: '',
            torque: '',
        },
        fuelConsumption: {
            tankCapacity: '',
            cityConsumption: '',
            highwayConsumption: '',
        },
        dimensions: {
            tireSize: '',
            height: '',
            length: '',
            luggageCapacity: '',
            emptyWeight: '',
            loadedWeight: '',
            width: '',
        },
        bodyCondition: {
            frontBumper: '',
            rearBumper: '',
            roof: '',
            trunkLid: '',
            engineHood: '',
            leftFrontDoor: '',
            rightFrontDoor: '',
            leftRearDoor: '',
            rightRearDoor: '',
            leftFrontFender: '',
            rightFrontFender: '',
            leftRearFender: '',
            rightRearFender: '',
        }
    };

    const [vehicle, setVehicle] = useState(initialVehicleState);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Çoklu fotoğraf yükleme için state'ler
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [imageLoading, setImageLoading] = useState(false);
    const [imageError, setImageError] = useState('');

    // Araç verilerini getir
    useEffect(() => {
        setLoading(true);

        // Fetch vehicle data inline instead of using the external function
        getVehicleById(id)
            .then(response => {
                console.log('API yanıtı:', response); // Debug için

                if (response && response.success && response.vehicle) {
                    const vehicleData = response.vehicle;
                    console.log('Araç verileri:', vehicleData); // Debug için

                    // Eksik alanları ekle
                    vehicleData.generalFeatures = vehicleData.generalFeatures || {};
                    vehicleData.engineDetails = vehicleData.engineDetails || {};
                    vehicleData.fuelConsumption = vehicleData.fuelConsumption || {};
                    vehicleData.dimensions = vehicleData.dimensions || {};
                    vehicleData.bodyCondition = vehicleData.bodyCondition || {};

                    // Features dizisini string'e dönüştür
                    const featuresString = Array.isArray(vehicleData.features)
                        ? vehicleData.features.join(', ')
                        : '';

                    // Verileri forma uygun formata dönüştür
                    setVehicle({
                        ...vehicleData,
                        features: featuresString
                    });

                    // ÖNEMLİ: Resim önizlemelerini temizleyelim önce
                    setImagePreviews([]);

                    // Yeni resim önizlemeleri oluşturalım
                    const previewImages = [];

                    // Ana resim varsa ekle
                    if (vehicleData.imageUrl && vehicleData.imageUrl !== '/uploads/default.png') {
                        const fullUrl = vehicleData.imageUrl.startsWith('http')
                            ? vehicleData.imageUrl
                            : `${vehicleData.imageUrl}`;
                        previewImages.push(fullUrl);
                        console.log('Ana resim eklendi:', fullUrl);
                    }

                    // Diğer resimler varsa ekle
                    if (vehicleData.images && vehicleData.images.length > 0) {
                        console.log('Images dizisi:', vehicleData.images);
                        vehicleData.images.forEach(img => {
                            if (img && img !== '/uploads/default.png') {
                                const fullUrl = img.startsWith('http')
                                    ? img
                                    : `${img}`;

                                // Eğer bu URL zaten previewImages içinde yoksa ekle
                                if (!previewImages.includes(fullUrl)) {
                                    previewImages.push(fullUrl);
                                    console.log('Ek resim eklendi:', fullUrl);
                                }
                            }
                        });
                    }

                    // Tüm önizleme resimlerini set et
                    console.log('Tüm önizleme resimleri:', previewImages);
                    setImagePreviews(previewImages);
                } else {
                    console.error('Beklenen API yanıt yapısı bulunamadı:', response);
                    setError('API yanıtında beklenen veri yapısı bulunamadı');
                }

                setLoading(false);
            })
            .catch(err => {
                setError('Araç bilgileri alınırken bir hata oluştu.');
                console.error('Araç getirme hatası:', err);
                setLoading(false);
            });
    }, [id]); // Only depend on id

    // Form değişikliklerini izle
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setVehicle(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setVehicle(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Resim dosyaları seçildiğinde
    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Dosya türü ve boyut kontrolü
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        const invalidFiles = files.filter(file =>
            !validTypes.includes(file.type) || file.size > maxSize
        );

        if (invalidFiles.length > 0) {
            setImageError('Bazı dosyalar geçersiz format veya boyutta. Lütfen sadece JPG, JPEG, PNG veya WEBP formatında, maksimum 5MB boyutunda dosyalar seçin.');
            return;
        }

        setImageError('');
        setImageFiles(prev => [...prev, ...files]);

        // Resim önizlemeleri
        files.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviews(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    // Önizleme resmini temizle
    const handleRemoveImage = (index) => {
        // Mevcut resim URL'si
        const imageToRemove = imagePreviews[index];
        console.log('Silinecek resim:', imageToRemove);

        // Cloudinary URL'si mi kontrol et
        const isCloudinaryUrl = imageToRemove && imageToRemove.includes('cloudinary.com');
        console.log('Cloudinary URL mi?', isCloudinaryUrl);

        if (vehicle._id) {
            // Silmeden önce kullanıcıya sor
            if (window.confirm('Bu fotoğrafı silmek istediğinizden emin misiniz?')) {
                setImageLoading(true);

                console.log('Silme isteği gönderiliyor, index:', index);
                console.log('Silinecek resim URL:', imageToRemove);

                axios.delete(
                    `/api/images/vehicles/${id}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        data: {
                            imageUrl: imageToRemove // Tam URL'yi gönder
                        }
                    }
                )
                    .then(response => {
                        console.log('Silme yanıtı:', response.data);

                        if (response.data.success) {
                            // 1. Önizlemeden kaldır
                            setImagePreviews(prev => prev.filter((_, i) => i !== index));

                            // 2. Vehicle state'inden kaldır
                            setVehicle(prev => {
                                const newVehicle = { ...prev };

                                // Cloudinary için tam URL kontrolü
                                if (isCloudinaryUrl) {
                                    if (newVehicle.images && Array.isArray(newVehicle.images)) {
                                        // Tam eşleşme ile kaldır
                                        newVehicle.images = newVehicle.images.filter(img => img !== imageToRemove);
                                    }

                                    // Ana resim de bu ise kaldır
                                    if (newVehicle.imageUrl === imageToRemove) {
                                        newVehicle.imageUrl = newVehicle.images.length > 0
                                            ? newVehicle.images[0]
                                            : '/uploads/vehicles/default.jpg';
                                    }
                                } else {
                                    // Yerel resimler için göreceli path kontrolü
                                    const relativePath = imageToRemove.replace('', '');

                                    if (newVehicle.images && Array.isArray(newVehicle.images)) {
                                        // Göreceli yol ile kaldır
                                        newVehicle.images = newVehicle.images.filter(img =>
                                            !img.includes(relativePath));
                                    }

                                    // Ana resim de bu ise kaldır
                                    if (newVehicle.imageUrl && newVehicle.imageUrl.includes(relativePath)) {
                                        newVehicle.imageUrl = newVehicle.images.length > 0
                                            ? newVehicle.images[0]
                                            : '/uploads/vehicles/default.jpg';
                                    }
                                }

                                return newVehicle;
                            });

                            // Verileri yeniden çek - ÖNEMLI: Cloudinary default URL'sini engellemek için
                            getVehicleById(id)
                                .then(response => {
                                    if (response && response.success && response.vehicle) {
                                        // Yeni araç verisi
                                        const vehicleData = { ...response.vehicle };

                                        // Eksik alanları ekle
                                        vehicleData.generalFeatures = vehicleData.generalFeatures || {};
                                        vehicleData.engineDetails = vehicleData.engineDetails || {};
                                        vehicleData.fuelConsumption = vehicleData.fuelConsumption || {};
                                        vehicleData.dimensions = vehicleData.dimensions || {};
                                        vehicleData.bodyCondition = vehicleData.bodyCondition || {};

                                        // Cloudinary default URL'sini filtrele
                                        if (vehicleData.images && Array.isArray(vehicleData.images)) {
                                            // Cloudinary default URL'sini kaldır
                                            vehicleData.images = vehicleData.images.filter(img =>
                                                !img.includes('default_zxlbnq.webp'));
                                        }

                                        // imageUrl da default Cloudinary ise, yerel default'a çevir
                                        if (vehicleData.imageUrl && vehicleData.imageUrl.includes('default_zxlbnq.webp')) {
                                            vehicleData.imageUrl = '/uploads/vehicles/default.jpg';
                                        }

                                        // Features dizisini string'e dönüştür
                                        const featuresString = Array.isArray(vehicleData.features)
                                            ? vehicleData.features.join(', ')
                                            : '';

                                        // Verileri forma uygun formata dönüştür
                                        setVehicle({
                                            ...vehicleData,
                                            features: featuresString
                                        });

                                        // Resim önizlemelerini güncelle
                                        const previewImages = [];

                                        // Ana resim varsa ekle
                                        if (vehicleData.imageUrl &&
                                            vehicleData.imageUrl !== '/uploads/vehicles/default.jpg' &&
                                            !vehicleData.imageUrl.includes('default_zxlbnq.webp')) {

                                            const fullUrl = vehicleData.imageUrl.startsWith('http')
                                                ? vehicleData.imageUrl
                                                : `${vehicleData.imageUrl}`;

                                            previewImages.push(fullUrl);
                                        }

                                        // Diğer resimler varsa ekle
                                        if (vehicleData.images && vehicleData.images.length > 0) {
                                            vehicleData.images.forEach(img => {
                                                // Default ve Cloudinary default resimlerini atla
                                                if (img &&
                                                    img !== '/uploads/vehicles/default.jpg' &&
                                                    !img.includes('default_zxlbnq.webp')) {

                                                    const fullUrl = img.startsWith('http')
                                                        ? img
                                                        : `${img}`;

                                                    // Eğer bu URL zaten previewImages içinde yoksa ekle
                                                    if (!previewImages.includes(fullUrl)) {
                                                        previewImages.push(fullUrl);
                                                    }
                                                }
                                            });
                                        }

                                        console.log('Güncellenmiş önizleme resimleri:', previewImages);
                                        setImagePreviews(previewImages);
                                    }
                                })
                                .catch(err => {
                                    console.error('Resim silme sonrası veri güncelleme hatası:', err);
                                });
                        } else {
                            setImageError('Resim silinirken bir hata oluştu: ' + response.data.message);
                        }

                        setImageLoading(false);
                    })
                    .catch(error => {
                        console.error('Resim silme hatası:', error);
                        setImageError('Resim silinirken bir hata oluştu.');
                        setImageLoading(false);
                    });
            }
        } else {
            // Sadece yerel önizleme resmini ve dosyasını kaldır
            setImagePreviews(prev => prev.filter((_, i) => i !== index));
            setImageFiles(prev => prev.filter((_, i) => i !== index));
        }
    };

    // Resimleri yükleme
    const uploadImages = async () => {
        if (imageFiles.length === 0) return [];

        setImageLoading(true);
        setImageError('');
        setUploadProgress(0);

        try {
            // Tüm resimleri yüklemek için promises dizisi oluştur
            const uploadPromises = imageFiles.map(async (file, index) => {
                const formData = new FormData();
                formData.append('image', file);

                console.log(`Dosya ${index + 1} yükleme isteği gönderiliyor`);
                console.log('Token:', localStorage.getItem('token'));

                const response = await axios.post(
                    `/api/images/vehicles/${id}`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        onUploadProgress: (progressEvent) => {
                            // Her dosya için ilerleme hesapla
                            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                            console.log(`Dosya ${index + 1} yükleniyor: %${progress}`);
                            setUploadProgress(progress);
                        }
                    }
                );

                console.log('Yükleme yanıtı:', response.data);

                // API yanıt yapısını kontrol et
                if (response.data && response.data.success && response.data.data && response.data.data.imageUrl) {
                    return response.data.data.imageUrl;
                }

                // Eğer beklenen yanıt yapısında değilse
                console.error('Beklenmeyen API yanıt yapısı:', response.data);
                return null;
            });

            // Tüm yüklemeleri bekle
            const uploadedImageUrls = await Promise.all(uploadPromises);

            // null değerleri filtrele
            const validUrls = uploadedImageUrls.filter(url => url !== null);

            setImageLoading(false);
            setUploadProgress(100);

            // Başarılı yükleme sonrası state'leri temizle
            setImageFiles([]);

            console.log('Tüm resimler yüklendi:', validUrls);

            // Yükleme sonrası araç verilerini yeniden çek
            await getVehicleById(id)
                .then(response => {
                    if (response && response.success && response.vehicle) {
                        const vehicleData = response.vehicle;

                        // Eksik alanları ekle
                        vehicleData.generalFeatures = vehicleData.generalFeatures || {};
                        vehicleData.engineDetails = vehicleData.engineDetails || {};
                        vehicleData.fuelConsumption = vehicleData.fuelConsumption || {};
                        vehicleData.dimensions = vehicleData.dimensions || {};
                        vehicleData.bodyCondition = vehicleData.bodyCondition || {};

                        // Features dizisini string'e dönüştür
                        const featuresString = Array.isArray(vehicleData.features)
                            ? vehicleData.features.join(', ')
                            : '';

                        // Verileri forma uygun formata dönüştür
                        setVehicle({
                            ...vehicleData,
                            features: featuresString
                        });

                        // ÖNEMLİ: Resim önizlemelerini güncelle
                        const previewImages = [];

                        // Ana resim varsa ekle
                        if (vehicleData.imageUrl && vehicleData.imageUrl !== '/uploads/default.png') {
                            const fullUrl = vehicleData.imageUrl.startsWith('http')
                                ? vehicleData.imageUrl
                                : `${vehicleData.imageUrl}`;
                            previewImages.push(fullUrl);
                        }

                        // Diğer resimler varsa ekle
                        if (vehicleData.images && vehicleData.images.length > 0) {
                            vehicleData.images.forEach(img => {
                                if (img && img !== '/uploads/default.png') {
                                    const fullUrl = img.startsWith('http')
                                        ? img
                                        : `${img}`;

                                    // Eğer bu URL zaten previewImages içinde yoksa ekle
                                    if (!previewImages.includes(fullUrl)) {
                                        previewImages.push(fullUrl);
                                    }
                                }
                            });
                        }

                        setImagePreviews(previewImages);
                    }
                })
                .catch(err => {
                    console.error('Resim yükleme sonrası veri güncelleme hatası:', err);
                });

            return validUrls;
        } catch (error) {
            console.error('Resim yükleme hatası:', error);
            setImageError('Resim yüklenirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
            setImageLoading(false);
            return [];
        }
    };

    // Form gönderme
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccessMessage('');

        try {
            // Araç verilerini hazırla
            let vehicleData = { ...vehicle };

            // Sayısal alanları dönüştür
            if (vehicleData.year) vehicleData.year = Number(vehicleData.year);
            if (vehicleData.price) vehicleData.price = Number(vehicleData.price);
            if (vehicleData.mileage) vehicleData.mileage = Number(vehicleData.mileage);

            // Features alanını diziye dönüştür
            if (typeof vehicleData.features === 'string') {
                vehicleData.features = vehicleData.features.split(',').map(item => item.trim());
            }

            // Önce yeni görüntüleri yükle (varsa)
            if (imageFiles.length > 0) {
                await uploadImages();
            }

            // Resim URL'lerini kullanıcının gördüğü önizleme resimlerinden al
            // Mevcut veritabanından alınan görüntüleri kullan, base64 imajları dahil etme
            const filteredPreviews = imagePreviews.filter(url =>
                !url.startsWith('data:image') && !url.includes('default_zxlbnq.webp')
            );

            vehicleData.images = filteredPreviews.map(url => {
                if (url.includes('')) {
                    return url.replace('', '');
                }
                return url;
            });

            // Formdan alınması gereken teknik detayları kontrol et ve düzenle
            // Bu alanların undefined veya null olmaması için koruma
            if (!vehicleData.generalFeatures) vehicleData.generalFeatures = {};
            if (!vehicleData.engineDetails) vehicleData.engineDetails = {};
            if (!vehicleData.fuelConsumption) vehicleData.fuelConsumption = {};
            if (!vehicleData.dimensions) vehicleData.dimensions = {};
            if (!vehicleData.bodyCondition) vehicleData.bodyCondition = {};

            // Verileri konsola yazarak kontrol et
            console.log("Gönderilecek araç verileri (özet):", {
                id: id,
                title: vehicleData.title,
                generalFeatures: vehicleData.generalFeatures,
                engineDetails: vehicleData.engineDetails,
                fuelConsumption: vehicleData.fuelConsumption,
                dimensions: vehicleData.dimensions,
                bodyCondition: vehicleData.bodyCondition
            });

            // TÜM NESNEYİ DE YAZDIR
            console.log("Gönderilecek tüm araç verileri:", JSON.stringify(vehicleData, null, 2));

            // Aracı güncelle
            console.log('Aracı güncelleme isteği gönderiliyor...');
            const response = await updateVehicle(id, vehicleData);
            console.log('Güncelleme yanıtı:', response);

            if (response && response.success) {
                setSuccessMessage('Araç başarıyla güncellendi!');

                // Araç detay sayfasına yönlendir
                setTimeout(() => {
                    navigate(`/vehicles/${id}`);
                }, 1500);
            } else {
                setError(response?.message || 'Araç güncellenirken bir hata oluştu');
            }
        } catch (err) {
            setError('Araç kaydedilirken bir hata oluştu: ' + (err.response?.data?.message || err.message));
            console.error('Araç kaydetme hatası:', err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            <h1 className="mb-4">Araç Düzenle</h1>

            {error && <Alert variant="danger">{error}</Alert>}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}

            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={8}>
                        {/* Modüler Form Bileşenleri */}
                        <VehicleBasicInfoForm data={vehicle} handleChange={handleChange} />
                        <VehicleTechnicalForm data={vehicle} handleChange={handleChange} />
                        <VehicleDetailedTechnicalForm data={vehicle} handleChange={handleChange} />
                        <VehicleBodyConditionForm data={vehicle} handleChange={handleChange} />
                        <VehicleAdditionalForm data={vehicle} handleChange={handleChange} />
                    </Col>

                    <Col md={4}>
                        {/* Fotoğraf Bölümü */}
                        <VehiclePhotoUpload
                            imagePreviews={imagePreviews}
                            handleImageChange={handleImageChange}
                            handleRemoveImage={handleRemoveImage}
                            imageLoading={imageLoading}
                            imageError={imageError}
                            uploadProgress={uploadProgress}
                            uploadImages={uploadImages}
                            isEditMode={true}
                            imageFiles={imageFiles}
                        />

                        {/* İşlem Butonları */}
                        <div className="d-grid gap-2">
                            <Button
                                variant="primary"
                                type="submit"
                                size="lg"
                                disabled={saving || imageLoading}
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Kaydediliyor...
                                    </>
                                ) : (
                                    'Güncelle'
                                )}
                            </Button>

                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(`/vehicles/${id}`)}
                                size="lg"
                            >
                                İptal
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Container>
    );
};

export default EditVehiclePage;